// Configuración de permisos por plan
// Define qué módulos y rutas están disponibles para cada plan

export type PlanId = 'facturacion-prepago-75' | 'facturacion-prepago-150' | 'facturacion-prepago-350' | 'facturacion-prepago-750' | 'facturacion-150' | 'facturacion-200' | 'facturacion-300' | 'facturacion-500' | 'facturacion-1000' | 'facturacion-simple' | 'facturacion-premium' | 'pos-premium' | 'pyme' | 'pro' | 'plus' | 'trial' | 'none';

export interface PlanLimits {
  users: number;
  /**
   * Facturas incluidas. -1 = sin tope.
   *
   * Sirve para pintar la interfaz, no para permitir ni negar nada: quien aplica el cupo es la
   * base de datos (`public.plan_limits` y el disparador `invoices_enforce_plan_limit`, migración
   * 014). Si los dos números discrepan, manda el de la base.
   *
   * En los planes prepago el nombre miente un poco: no son N al mes, son N en total con vigencia
   * de un año. La distinción sí la hace la base, en la columna `quota_kind`.
   */
  invoicesPerMonth: number;
  warehouses: number;
  products: number;
}

export interface PlanConfig {
  id: PlanId;
  name: string;
  modules: string[];
  routes: string[];
  limits: PlanLimits;
}

// Módulos del sistema
export const MODULES = {
  DASHBOARD: 'dashboard',
  // Videoconferencias. Va en todos los planes de pago y en la prueba: no es una
  // capacidad contable que distinga un plan de otro, es una herramienta de
  // trabajo, y cobrarla aparte obligaría a explicar por qué el plan de 75
  // facturas no puede llamar por vídeo a su contador.
  VIDEOCONFERENCIA: 'videoconferencia',
  BILLING: 'billing',
  INVOICING: 'invoicing',
  QUOTES: 'quotes',
  CREDIT_NOTES: 'credit-notes',
  REPORTS: 'reports',
  INVENTORY: 'inventory',
  PRODUCTS: 'products',
  INVENTORY_REPORTS: 'inventory-reports',
  INVENTORY_WAREHOUSES: 'inventory-warehouses',
  INVENTORY_TRANSFERS: 'inventory-transfers',
  POS: 'pos',
  CUSTOMERS: 'customers',
  CUSTOMER_MANAGEMENT: 'customer-management',
  SUPPLIERS: 'suppliers',
  PURCHASES: 'purchases',
  EXPENSES: 'expenses',
  ACCOUNTS_RECEIVABLE: 'accounts-receivable',
  ACCOUNTS_PAYABLE: 'accounts-payable',
  PAYROLL: 'payroll',
  BANKS: 'banks',
  PETTY_CASH: 'petty-cash',
  COMMISSIONS: 'commissions',
  REPAIRS: 'repairs',
  RETURNS: 'returns',
  MULTI_BRANCH: 'multi-branch',
  MULTI_CURRENCY: 'multi-currency',
  ACCOUNTING: 'accounting',
  // Costos de producción. Módulo propio y no una subrruta de contabilidad: es lo que
  // permite venderlo como la funcionalidad exclusiva de PLUS.
  PRODUCTION: 'production',
  TAXES: 'taxes',
  SETTINGS: 'settings',
  USERS: 'users',
  FIXED_ASSETS: 'fixed-assets',
  STATISTICS: 'statistics',
  ACCOUNTING_SETTINGS: 'accounting-settings',
} as const;

// Configuración de cada plan
export const PLAN_CONFIGS: Record<PlanId, PlanConfig> = {
  'facturacion-prepago-75': {
    id: 'facturacion-prepago-75',
    name: 'Plan 75 Facturas',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.CUSTOMERS,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/customers',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 1,
      invoicesPerMonth: 75,
      warehouses: 0,
      products: 0,
    }
  },
  'facturacion-prepago-150': {
    id: 'facturacion-prepago-150',
    name: 'Plan 150 Facturas',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.CUSTOMERS,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/inventory',
      '/inventory/products',
      '/products',
      '/customers',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 1,
      invoicesPerMonth: 150,
      warehouses: 0,
      products: -1,
    }
  },
  'facturacion-prepago-350': {
    id: 'facturacion-prepago-350',
    name: 'Plan 350 Facturas',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.CUSTOMERS,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/inventory',
      '/inventory/products',
      '/products',
      '/customers',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 1,
      invoicesPerMonth: 350,
      warehouses: 0,
      products: -1,
    }
  },
  'facturacion-prepago-750': {
    id: 'facturacion-prepago-750',
    name: 'Plan 750 Facturas',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.CUSTOMERS,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/inventory',
      '/inventory/products',
      '/products',
      '/customers',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 1,
      invoicesPerMonth: 750,
      warehouses: 0,
      products: -1,
    }
  },
  // Los dos planes de entrada faltaban en esta tabla aunque llevan tiempo a la venta en
  // `planesPublicos.ts` y hay cuentas contratadas. Sin entrada aquí, `usePlanPermissions` los
  // daba por desconocidos y les concedía el paquete completo de POS Premium.
  'facturacion-150': {
    id: 'facturacion-150',
    name: 'Plan 150 Facturas',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.CUSTOMERS,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/inventory',
      '/inventory/products',
      '/products',
      '/customers',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 1,
      invoicesPerMonth: 150,
      warehouses: 0,
      products: -1,
    }
  },
  'facturacion-200': {
    id: 'facturacion-200',
    name: 'Plan 200 Facturas',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.CUSTOMERS,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/inventory',
      '/inventory/products',
      '/products',
      '/customers',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 1,
      invoicesPerMonth: 200,
      warehouses: 0,
      products: -1,
    }
  },
  'facturacion-300': {
    id: 'facturacion-300',
    name: 'Plan 300 Facturas',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.CUSTOMERS,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/inventory',
      '/inventory/products',
      '/products',
      '/customers',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 1,
      invoicesPerMonth: 300,
      warehouses: 0,
      products: -1,
    }
  },
  'facturacion-500': {
    id: 'facturacion-500',
    name: 'Plan 500 Facturas',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.CUSTOMERS,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/inventory',
      '/inventory/products',
      '/products',
      '/customers',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 1,
      invoicesPerMonth: 500,
      warehouses: 0,
      products: -1,
    }
  },
  'facturacion-1000': {
    id: 'facturacion-1000',
    name: 'Plan 1000 Facturas',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.INVENTORY_REPORTS,
      MODULES.CUSTOMERS,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/inventory',
      '/inventory/products',
      '/inventory/reports',
      '/products',
      '/customers',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 1,
      invoicesPerMonth: 1000,
      warehouses: 0,
      products: -1,
    }
  },
  'facturacion-simple': {
    id: 'facturacion-simple',
    name: 'Plan 2500 Facturas',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.INVENTORY_REPORTS,
      MODULES.CUSTOMERS,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/inventory',
      '/inventory/products',
      '/inventory/reports',
      '/products',
      '/customers',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 1,
      invoicesPerMonth: 2500,
      warehouses: 0,
      products: -1,
    }
  },
  'facturacion-premium': {
    id: 'facturacion-premium',
    name: 'Plan 3500 Facturas',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.INVENTORY_REPORTS,
      MODULES.INVENTORY_WAREHOUSES,
      MODULES.INVENTORY_TRANSFERS,
      MODULES.CUSTOMERS,
      MODULES.CUSTOMER_MANAGEMENT,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/products',
      '/inventory',
      '/inventory/products',
      '/inventory/warehouses',
      '/customers',
      '/accounts-receivable/customers',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 8,
      invoicesPerMonth: 3500,
      warehouses: 1,
      products: 1000,
    }
  },
  'pos-premium': {
    id: 'pos-premium',
    name: 'POS Premium',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.POS,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.INVENTORY_REPORTS,
      MODULES.INVENTORY_WAREHOUSES,
      MODULES.INVENTORY_TRANSFERS,
      MODULES.CUSTOMERS,
      MODULES.CUSTOMER_MANAGEMENT,
      MODULES.SUPPLIERS,
      MODULES.PURCHASES,
      MODULES.EXPENSES,
      MODULES.ACCOUNTS_RECEIVABLE,
      MODULES.ACCOUNTS_PAYABLE,
      MODULES.PAYROLL,
      MODULES.BANKS,
      MODULES.PETTY_CASH,
      MODULES.COMMISSIONS,
      MODULES.REPAIRS,
      MODULES.RETURNS,
      MODULES.MULTI_BRANCH,
      MODULES.ACCOUNTING,
      MODULES.PRODUCTION,
      MODULES.TAXES,
      MODULES.SETTINGS,
      MODULES.USERS,
      MODULES.FIXED_ASSETS,
      MODULES.STATISTICS,
      MODULES.ACCOUNTING_SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/pos',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/billing/recurring',
      '/reports',
      '/products',
      '/inventory',
      '/inventory/products',
      '/inventory/warehouses',
      '/inventory/movements',
      '/customers',
      '/suppliers',
      '/purchases',
      '/expenses',
      '/accounts-receivable',
      '/accounts-payable',
      '/payroll',
      '/banks',
      '/banks-module',
      '/petty-cash',
      '/commissions',
      '/repairs',
      '/returns',
      '/accounting',
      '/taxes',
      '/settings',
      '/settings/accounting',
      '/users',
      '/fixed-assets',
      '/statistics',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 80,
      invoicesPerMonth: 2000,
      warehouses: 2,
      products: -1, // ilimitado
    }
  },
  'pyme': {
    id: 'pyme',
    name: 'PYME',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.POS,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.INVENTORY_REPORTS,
      MODULES.INVENTORY_WAREHOUSES,
      MODULES.INVENTORY_TRANSFERS,
      MODULES.CUSTOMERS,
      MODULES.CUSTOMER_MANAGEMENT,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/pos',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/inventory',
      '/inventory/products',
      '/products',
      '/customers',
      '/accounts-receivable/customers',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 2,
      invoicesPerMonth: -1, // ilimitado
      warehouses: 1,
      products: 500, // Inventario limitado (500)
    }
  },
  'pro': {
    id: 'pro',
    name: 'PRO',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.POS,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.INVENTORY_REPORTS,
      MODULES.INVENTORY_WAREHOUSES,
      MODULES.INVENTORY_TRANSFERS,
      MODULES.CUSTOMERS,
      MODULES.CUSTOMER_MANAGEMENT,
      MODULES.SUPPLIERS,
      MODULES.PURCHASES,
      MODULES.ACCOUNTS_RECEIVABLE,
      MODULES.ACCOUNTS_PAYABLE,
      MODULES.PAYROLL,
      MODULES.BANKS,
      MODULES.ACCOUNTING,
      MODULES.SETTINGS,
    ],
    routes: [
      '/dashboard',
      '/videoconferencia',
      '/pos',
      '/billing',
      '/billing/invoicing',
      '/billing/quotes',
      '/billing/credit-notes',
      '/billing/debit-notes',
      '/reports',
      '/inventory',
      '/inventory/products',
      '/inventory/warehouses',
      '/products',
      '/customers',
      '/suppliers',
      '/purchases',
      '/accounts-receivable',
      '/accounts-payable',
      '/payroll',
      '/banks',
      '/banks-module',
      '/accounting',
      '/settings',
      '/profile',
      '/plans',
    ],
    limits: {
      users: 5,
      invoicesPerMonth: -1, // ilimitado
      warehouses: 1,
      products: 2000, // Inventario limitado (2,000)
    }
  },
  'plus': {
    id: 'plus',
    name: 'PLUS',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.POS,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.INVENTORY_REPORTS,
      MODULES.INVENTORY_WAREHOUSES,
      MODULES.INVENTORY_TRANSFERS,
      MODULES.CUSTOMERS,
      MODULES.CUSTOMER_MANAGEMENT,
      MODULES.SUPPLIERS,
      MODULES.PURCHASES,
      MODULES.EXPENSES,
      MODULES.ACCOUNTS_RECEIVABLE,
      MODULES.ACCOUNTS_PAYABLE,
      MODULES.PAYROLL,
      MODULES.BANKS,
      MODULES.PETTY_CASH,
      MODULES.COMMISSIONS,
      MODULES.REPAIRS,
      MODULES.RETURNS,
      MODULES.MULTI_BRANCH,
      MODULES.ACCOUNTING,
      MODULES.PRODUCTION,
      MODULES.TAXES,
      MODULES.SETTINGS,
      MODULES.USERS,
      MODULES.FIXED_ASSETS,
      MODULES.STATISTICS,
      MODULES.ACCOUNTING_SETTINGS,
    ],
    routes: ['*'], // Todas las rutas - todas las funciones
    limits: {
      // El comentario decía «Empresas ilimitadas» sobre el número de usuarios, que son dos cosas
      // distintas. El tope de empresas no vive en esta tabla: está en
      // `plan_limits.max_companies` (la base, que es quien lo aplica) y en `usePlanLimitations`
      // para pintarlo. PLUS es de una sola empresa: la multiempresa se retiró del plan y su
      // lugar en el catálogo lo ocupa el módulo de Producción (migración 031).
      users: -1, // ilimitado
      invoicesPerMonth: -1, // ilimitado
      warehouses: -1, // ilimitado
      products: -1, // Inventario ilimitado
    }
  },
  'trial': {
    id: 'trial',
    name: 'Prueba Gratuita',
    modules: [
      MODULES.DASHBOARD,
      MODULES.VIDEOCONFERENCIA,
      MODULES.POS,
      MODULES.BILLING,
      MODULES.INVOICING,
      MODULES.QUOTES,
      MODULES.CREDIT_NOTES,
      MODULES.REPORTS,
      MODULES.INVENTORY,
      MODULES.PRODUCTS,
      MODULES.INVENTORY_REPORTS,
      MODULES.INVENTORY_WAREHOUSES,
      MODULES.INVENTORY_TRANSFERS,
      MODULES.CUSTOMERS,
      MODULES.CUSTOMER_MANAGEMENT,
      MODULES.SUPPLIERS,
      MODULES.PURCHASES,
      MODULES.EXPENSES,
      MODULES.ACCOUNTS_RECEIVABLE,
      MODULES.ACCOUNTS_PAYABLE,
      MODULES.PAYROLL,
      MODULES.BANKS,
      MODULES.PETTY_CASH,
      MODULES.COMMISSIONS,
      MODULES.REPAIRS,
      MODULES.RETURNS,
      MODULES.MULTI_BRANCH,
      MODULES.ACCOUNTING,
      MODULES.PRODUCTION,
      MODULES.TAXES,
      MODULES.SETTINGS,
      MODULES.USERS,
      MODULES.FIXED_ASSETS,
      MODULES.ACCOUNTING_SETTINGS,
    ],
    routes: ['*'], // Todas las rutas durante prueba
    limits: {
      users: 5,
      invoicesPerMonth: 50,
      warehouses: 1,
      products: 100,
    }
  },
  'none': {
    id: 'none',
    name: 'Sin Plan',
    modules: [],
    routes: ['/plans', '/profile', '/settings'],
    limits: {
      users: 0,
      invoicesPerMonth: 0,
      warehouses: 0,
      products: 0,
    }
  }
};

// Mapeo de rutas a módulos para verificación
export const ROUTE_TO_MODULE: Record<string, string> = {
  '/dashboard': MODULES.DASHBOARD,
  '/videoconferencia': MODULES.VIDEOCONFERENCIA,
  '/pos': MODULES.POS,
  '/billing': MODULES.BILLING,
  '/billing/invoicing': MODULES.INVOICING,
  '/billing/quotes': MODULES.QUOTES,
  '/billing/credit-notes': MODULES.CREDIT_NOTES,
  '/billing/debit-notes': MODULES.CREDIT_NOTES,
  '/billing/recurring': MODULES.BILLING,
  '/reports': MODULES.REPORTS,
  '/inventory': MODULES.INVENTORY,
  '/inventory/products': MODULES.PRODUCTS,
  '/products': MODULES.PRODUCTS,
  '/inventory/reports': MODULES.INVENTORY_REPORTS,
  '/inventory/physical-count': MODULES.INVENTORY_REPORTS,
  '/inventory/physical-result': MODULES.INVENTORY_REPORTS,
  '/inventory/cost-revaluation': MODULES.INVENTORY_REPORTS,
  '/inventory/warehouses': MODULES.INVENTORY_WAREHOUSES,
  '/inventory/transfers': MODULES.INVENTORY_TRANSFERS,
  '/inventory/delivery-notes': MODULES.INVENTORY_TRANSFERS,
  '/inventory/movements': MODULES.INVENTORY,
  '/production': MODULES.PRODUCTION,
  '/production/boms': MODULES.PRODUCTION,
  '/production/orders': MODULES.PRODUCTION,
  '/production/variances': MODULES.PRODUCTION,
  '/production/overhead': MODULES.PRODUCTION,
  '/customers': MODULES.CUSTOMERS,
  '/suppliers': MODULES.SUPPLIERS,
  '/purchases': MODULES.PURCHASES,
  '/expenses': MODULES.EXPENSES,
  '/accounts-receivable': MODULES.ACCOUNTS_RECEIVABLE,
  '/accounts-receivable/invoices': MODULES.ACCOUNTS_RECEIVABLE,
  '/accounts-receivable/customers': MODULES.CUSTOMER_MANAGEMENT,
  '/accounts-receivable/customer-types': MODULES.ACCOUNTS_RECEIVABLE,
  '/accounts-receivable/payment-terms': MODULES.ACCOUNTS_RECEIVABLE,
  '/accounts-receivable/payments': MODULES.ACCOUNTS_RECEIVABLE,
  '/accounts-receivable/receipts': MODULES.ACCOUNTS_RECEIVABLE,
  '/accounts-receivable/advances': MODULES.ACCOUNTS_RECEIVABLE,
  '/accounts-receivable/discounts': MODULES.ACCOUNTS_RECEIVABLE,
  '/accounts-receivable/credit-notes': MODULES.ACCOUNTS_RECEIVABLE,
  '/accounts-receivable/debit-notes': MODULES.ACCOUNTS_RECEIVABLE,
  '/accounts-receivable/reports': MODULES.ACCOUNTS_RECEIVABLE,
  '/accounts-payable': MODULES.ACCOUNTS_PAYABLE,
  '/payroll': MODULES.PAYROLL,
  '/banks': MODULES.BANKS,
  '/banks-module': MODULES.BANKS,
  '/petty-cash': MODULES.PETTY_CASH,
  '/commissions': MODULES.COMMISSIONS,
  '/repairs': MODULES.REPAIRS,
  '/returns': MODULES.RETURNS,
  '/accounting': MODULES.ACCOUNTING,
  '/taxes': MODULES.TAXES,
  '/settings': MODULES.SETTINGS,
  '/users': MODULES.USERS,
  '/fixed-assets': MODULES.FIXED_ASSETS,
  '/statistics': MODULES.STATISTICS,
  '/settings/accounting': MODULES.ACCOUNTING_SETTINGS,
};

// Nombres amigables para los módulos
export const MODULE_NAMES: Record<string, string> = {
  [MODULES.DASHBOARD]: 'Dashboard',
  [MODULES.VIDEOCONFERENCIA]: 'Videoconferencias',
  [MODULES.POS]: 'Punto de Venta (POS)',
  [MODULES.BILLING]: 'Facturación',
  [MODULES.INVOICING]: 'Facturas',
  [MODULES.QUOTES]: 'Cotizaciones',
  [MODULES.CREDIT_NOTES]: 'Notas de Crédito/Débito',
  [MODULES.REPORTS]: 'Reportes',
  [MODULES.INVENTORY]: 'Inventario',
  [MODULES.PRODUCTS]: 'Productos',
  [MODULES.INVENTORY_REPORTS]: 'Reportes de inventario',
  [MODULES.INVENTORY_WAREHOUSES]: 'Almacenes',
  [MODULES.INVENTORY_TRANSFERS]: 'Transferencias de inventario',
  [MODULES.CUSTOMERS]: 'Clientes',
  [MODULES.CUSTOMER_MANAGEMENT]: 'Gestión completa de clientes',
  [MODULES.SUPPLIERS]: 'Proveedores',
  [MODULES.PURCHASES]: 'Compras',
  [MODULES.EXPENSES]: 'Gastos',
  [MODULES.ACCOUNTS_RECEIVABLE]: 'Cuentas por Cobrar',
  [MODULES.ACCOUNTS_PAYABLE]: 'Cuentas por Pagar',
  [MODULES.PAYROLL]: 'Nómina',
  [MODULES.BANKS]: 'Gestión Bancaria',
  [MODULES.PETTY_CASH]: 'Caja Chica',
  [MODULES.COMMISSIONS]: 'Comisiones',
  [MODULES.REPAIRS]: 'Servicio de Reparaciones',
  [MODULES.RETURNS]: 'Devoluciones',
  [MODULES.MULTI_BRANCH]: 'Multisucursal',
  [MODULES.MULTI_CURRENCY]: 'Multimoneda',
  [MODULES.ACCOUNTING]: 'Contabilidad',
  [MODULES.PRODUCTION]: 'Producción',
  [MODULES.TAXES]: 'Impuestos',
  [MODULES.SETTINGS]: 'Configuración',
  [MODULES.USERS]: 'Usuarios',
  [MODULES.FIXED_ASSETS]: 'Activos Fijos',
  [MODULES.STATISTICS]: 'Estadísticas',
  [MODULES.ACCOUNTING_SETTINGS]: 'Configuración Contable',
};

// Función para obtener el plan mínimo requerido para un módulo
export function getMinimumPlanForModule(module: string): PlanId {
  // Módulos básicos disponibles desde Facturación Simple
  const basicModules: string[] = [MODULES.DASHBOARD, MODULES.BILLING, MODULES.INVOICING, MODULES.QUOTES, MODULES.CREDIT_NOTES, MODULES.REPORTS, MODULES.SETTINGS];
  if (basicModules.includes(module)) return 'facturacion-300';

  // Videoconferencias: incluida en todos los planes, también en el más barato.
  if (module === MODULES.VIDEOCONFERENCIA) return 'facturacion-prepago-75';

  // Crear clientes está incluido desde cualquier plan de facturación.
  if (module === MODULES.CUSTOMERS) return 'facturacion-prepago-75';

  // Editar, exportar y administrar clientes sigue siendo una capacidad contable.
  if (module === MODULES.CUSTOMER_MANAGEMENT) return 'pyme';

  // El inventario básico se incluye desde los planes de facturación indicados en la matriz.
  const inventoryModules: string[] = [MODULES.INVENTORY, MODULES.PRODUCTS];
  if (inventoryModules.includes(module)) return 'facturacion-prepago-150';

  if (module === MODULES.INVENTORY_REPORTS) return 'facturacion-1000';
  if ([MODULES.INVENTORY_WAREHOUSES, MODULES.INVENTORY_TRANSFERS].includes(module)) return 'pyme';

  // Módulos contables / banca / nómina: mínimo PRO
  const accountingModules: string[] = [
    MODULES.ACCOUNTING,
    MODULES.ACCOUNTING_SETTINGS,
    MODULES.BANKS,
    MODULES.ACCOUNTS_RECEIVABLE,
    MODULES.ACCOUNTS_PAYABLE,
    MODULES.PURCHASES,
    MODULES.PAYROLL,
    MODULES.TAXES,
  ];
  if (accountingModules.includes(module)) return 'pro';

  // PYME incluye el punto de venta.
  if (module === MODULES.POS) return 'pyme';

  // Costos de producción: exclusivo de PLUS. Es la funcionalidad que sustituye a la multiempresa
  // en el catálogo del plan.
  if (module === MODULES.PRODUCTION) return 'plus';

  // Todos los demás módulos requieren POS Premium
  return 'plus';
}

// Función para obtener el nombre del plan mínimo requerido
export function getMinimumPlanName(module: string): string {
  const planId = getMinimumPlanForModule(module);
  return PLAN_CONFIGS[planId]?.name || 'Plan desconocido';
}
